import type { CoinMetricsRequestStream, CoinMetricsTickPayload } from '@src/providers/CoinMetricsApiProvider';
import { useCoinMetricsSubscription } from '@src/providers/CoinMetricsApiProvider';
import { useDebouncedValue, type MinimalSubscriptionResponse, type SubscriptionResponse } from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReplaySubject, type Observable } from 'rxjs';
import { bufferTime, filter } from 'rxjs/operators';
import type { AssetLeaderboardEntity, AssetLeaderboardPriceEnrichment } from './AssetLeaderboardEntity';
import {
  ASSET_LEADERBOARD_LIVE_PRICE_BATCH_MS,
  ASSET_LEADERBOARD_LIVE_PRICE_DEBOUNCE_MS,
  ASSET_LEADERBOARD_LIVE_PRICE_FREQUENCY,
  ASSET_LEADERBOARD_PRICE_COLUMN_ID,
  ASSET_LEADERBOARD_PRICE_METRIC,
} from './tokens';

export type AssetLeaderboardPriceEnrichmentMessage = {
  /** This batch's changed enrichments only (the delta) — what `useBlotterVisibleRowsEnrichment` applies
   * to currently-rendered rows right now. */
  json: MinimalSubscriptionResponse<AssetLeaderboardPriceEnrichment>;
  /** Every latest price seen so far, keyed by CoinMetrics asset id — lets a row scrolled back into view
   * re-hydrate its live price from cache without waiting for the next tick. */
  cache: Map<string, AssetLeaderboardPriceEnrichment>;
};

/**
 * Extracts `{ cmAssetId, price }` from one live-price `SubscriptionResponse`, or null if it's missing
 * either — defensive against a future metric sharing the same client/type ever landing here without a
 * price. `CoinMetricsTickPayload.metrics` carries CoinMetrics' raw decimal strings (never converted
 * generically — see its own doc), so the conversion to a number happens here, at the one place that
 * actually knows `ReferenceRateUSD` is safely representable as a double. Exported for unit testing.
 */
export function extractPriceUpdate(
  response: SubscriptionResponse<CoinMetricsTickPayload>
): AssetLeaderboardPriceEnrichment | null {
  const cmAssetId = response.tag ?? response.data[0]?.tag;
  const rawPrice = response.data[0]?.metrics[ASSET_LEADERBOARD_PRICE_METRIC];
  if (cmAssetId == null || rawPrice == null) {
    return null;
  }
  const price = Number(rawPrice);
  return Number.isFinite(price) ? { cmAssetId, price } : null;
}

/** Stable dedupe key for a visible-set snapshot, so identical viewport re-emissions don't churn the
 * subscription. Price-column visibility gates the whole stream (a hidden Price column needs no live
 * price at all), and the asset ids are sorted since order is irrelevant to what we subscribe to. */
function visibleSubscriptionKey(priceColumnVisible: boolean, cmAssetIds: string[]): string {
  return priceColumnVisible ? cmAssetIds.toSorted().join(',') : '';
}

/**
 * Produces the Asset Leaderboard's live-price enrichment stream for the blotter's visible-rows enrichment
 * pipeline (`useBlotterVisibleRowsEnrichment`). This is the modern, PriceGrid-style replacement for the
 * old Watchlist `useRenderedRows` + full-row-overlay approach: the visible rows and columns arrive as
 * an observable (`useVisibleCellsChanged`), and only the price cells of currently-visible rows are ever
 * subscribed.
 *
 * CoinMetrics-specific nuance preserved from the original: unlike Talos MDS (which amends a subscription
 * in place), CoinMetrics has no in-place amend — a change to the subscribed asset set means closing and
 * reopening the connection. So on top of `useVisibleCellsChanged`'s own 200ms viewport throttle, the
 * visible set is debounced by `ASSET_LEADERBOARD_LIVE_PRICE_DEBOUNCE_MS` before the stream reconnects,
 * trading a little responsiveness for connection stability. Do NOT rely on the 200ms throttle alone.
 *
 * The returned observable's `cache` is retained across reconnects (it lives in a ref, not inside the
 * rebuilt rxjs pipeline), so a row that briefly leaves and re-enters the viewport re-hydrates its live
 * price immediately instead of blanking back to the REST close until the next tick.
 */
export function useAssetLeaderboardPriceEnrichment(
  visibleCellsChangedObs: Observable<{ renderedItems: AssetLeaderboardEntity[]; visibleColumns: Column[] }>
): Observable<AssetLeaderboardPriceEnrichmentMessage> {
  const [visible, setVisible] = useState<{ cmAssetIds: string[]; priceColumnVisible: boolean }>({
    cmAssetIds: [],
    priceColumnVisible: false,
  });
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const subscription = visibleCellsChangedObs.subscribe(({ renderedItems, visibleColumns }) => {
      const priceColumnVisible = visibleColumns.some(column => column.getColId() === ASSET_LEADERBOARD_PRICE_COLUMN_ID);
      const cmAssetIds = renderedItems.map(item => item.cmAssetId);
      const key = visibleSubscriptionKey(priceColumnVisible, cmAssetIds);
      if (key === lastKeyRef.current) {
        return;
      }
      lastKeyRef.current = key;
      setVisible({ cmAssetIds, priceColumnVisible });
    });
    return () => subscription.unsubscribe();
  }, [visibleCellsChangedObs]);

  const debouncedVisible = useDebouncedValue(visible, ASSET_LEADERBOARD_LIVE_PRICE_DEBOUNCE_MS);

  const streams = useMemo<CoinMetricsRequestStream[] | null>(
    () =>
      !debouncedVisible.priceColumnVisible || debouncedVisible.cmAssetIds.length === 0
        ? null
        : debouncedVisible.cmAssetIds.map(tag => ({
            name: ASSET_LEADERBOARD_PRICE_METRIC,
            tag,
            frequency: ASSET_LEADERBOARD_LIVE_PRICE_FREQUENCY,
          })),
    [debouncedVisible]
  );

  const { data } = useCoinMetricsSubscription(streams);

  // The enrichment cache and its subject are kept stable across stream reconnects. Recreating them on
  // every reconnect would wipe the accumulated prices, so a still-visible row would blank until its next
  // tick. `ReplaySubject(1)` also lets a (re-)subscribing enrichment pipeline pick up the latest batch.
  const cacheRef = useRef<Map<string, AssetLeaderboardPriceEnrichment>>(new Map());
  const subjectRef = useRef<ReplaySubject<AssetLeaderboardPriceEnrichmentMessage> | null>(null);
  if (subjectRef.current == null) {
    subjectRef.current = new ReplaySubject<AssetLeaderboardPriceEnrichmentMessage>(1);
  }
  const subject = subjectRef.current;

  useEffect(() => {
    // Coalesce rapid successive ticks into one enrichment message instead of one per websocket message —
    // same rationale as Watchlist's own debounced live-price pipeline.
    const subscription = data
      .pipe(
        bufferTime(ASSET_LEADERBOARD_LIVE_PRICE_BATCH_MS),
        filter(batch => batch.length > 0)
      )
      .subscribe(batch => {
        const entries = batch
          .map(extractPriceUpdate)
          .filter((entry): entry is AssetLeaderboardPriceEnrichment => entry != null);
        if (entries.length === 0) {
          return;
        }
        const cache = new Map(cacheRef.current);
        for (const entry of entries) {
          cache.set(entry.cmAssetId, entry);
        }
        cacheRef.current = cache;
        subject.next({ json: { data: entries }, cache });
      });
    return () => subscription.unsubscribe();
  }, [data, subject]);

  return useMemo(() => subject.asObservable(), [subject]);
}
